import { useState, useEffect } from "react";
import { storageGet, storageSet } from "@/lib/uiStorage";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "lumora_sticky_cta_dismissed_v1";

export const StickyCTABar = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => storageGet(DISMISS_KEY) === "1");

  useEffect(() => {
    if (user || dismissed) return;
    const onScroll = () => {
      // Show after user scrolls past ~75% of viewport (past hero)
      const trigger = window.innerHeight * 0.75;
      setVisible(window.scrollY > trigger);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [user, dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    setVisible(false);
    storageSet(DISMISS_KEY, "1");
  };

  if (user || dismissed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed bottom-0 inset-x-0 z-40"
          data-testid="sticky-cta-bar"
        >
          <div className="px-4 md:px-8 py-2 backdrop-blur-2xl bg-gradient-to-r from-violet-600/95 via-indigo-600/95 to-purple-600/95 border-t border-white/20 shadow-2xl shadow-purple-900/40 flex items-center gap-3 max-w-7xl mx-auto w-full">
            <div className="hidden sm:flex w-8 h-8 rounded-full bg-white/15 items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm leading-tight">Start free — no credit card</p>
              <p className="text-white/70 text-xs hidden sm:block leading-tight mt-0.5">Finished 15s UGC ads in minutes · 30 free credits to start</p>
            </div>
            <Link
              to="/register"
              className="flex items-center gap-1.5 px-4 md:px-5 py-2 rounded-full bg-white text-purple-700 font-bold text-sm hover:bg-white/90 transition-all whitespace-nowrap shadow-lg"
              data-testid="sticky-cta-signup-btn"
            >
              Start Free <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="w-8 h-8 rounded-full text-white/70 hover:text-white hover:bg-white/10 flex items-center justify-center flex-shrink-0 transition-colors"
              data-testid="sticky-cta-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
